import { useEffect, useRef } from 'react';

export function useSwipeNavigation({
  onSwipeLeft,
  onSwipeRight,
  minDistance = 50,
  maxVerticalRatio = 1.5,
  enabled = true,
  targetRef = null
}) {
  const startRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    const target = targetRef && targetRef.current ? targetRef.current : window;
    const handleSwipe = (dx, dy) => {
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);
      if (absX > minDistance && absX > absY * maxVerticalRatio) {
        if (dx < 0 && typeof onSwipeLeft === 'function') onSwipeLeft();
        if (dx > 0 && typeof onSwipeRight === 'function') onSwipeRight();
      }
    };

    if (typeof window.PointerEvent !== 'undefined') {
      const onPointerDown = (e) => {
        if (!e) return;
        if (e.pointerType && e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
        startRef.current = { x: e.clientX, y: e.clientY };
      };

      const onPointerUp = (e) => {
        if (!e) return;
        if (e.pointerType && e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
        const start = startRef.current;
        handleSwipe(e.clientX - start.x, e.clientY - start.y);
      };

      const onPointerCancel = () => {
        startRef.current = { x: 0, y: 0 };
      };

      target.addEventListener('pointerdown', onPointerDown, { passive: true });
      target.addEventListener('pointerup', onPointerUp, { passive: true });
      target.addEventListener('pointercancel', onPointerCancel, { passive: true });
      return () => {
        target.removeEventListener('pointerdown', onPointerDown);
        target.removeEventListener('pointerup', onPointerUp);
        target.removeEventListener('pointercancel', onPointerCancel);
      };
    }

    const onTouchStart = (e) => {
      const t = e.touches && e.touches[0];
      if (!t) return;
      startRef.current = { x: t.clientX, y: t.clientY };
    };

    const onTouchEnd = (e) => {
      const t = e.changedTouches && e.changedTouches[0];
      if (!t) return;
      const start = startRef.current;
      handleSwipe(t.clientX - start.x, t.clientY - start.y);
    };

    target.addEventListener('touchstart', onTouchStart, { passive: true });
    target.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      target.removeEventListener('touchstart', onTouchStart);
      target.removeEventListener('touchend', onTouchEnd);
    };
  }, [enabled, maxVerticalRatio, minDistance, onSwipeLeft, onSwipeRight, targetRef]);
}
